import { graphql } from 'gatsby';
import React, { useContext } from 'react';

import LayoutContext from '../../../contexts/layoutContext';
import {
  Drupal_ParagraphLink,
  Drupal_ParagraphMarketoForm
} from '../../../generated/graphql';
import Link from '../../link/link';
import './nineteen-cta.scss';
import {
  MarketingPageElementLocation,
  MarketingPageElementType
} from '@lucid/gatsby-theme-lucid';
import useMarketoLink from '../../../hooks/use-marketo-link';

export const nineteenCtaThemes = {
  default: '',
  inverse: 'nineteen-cta--inverse',
  arrow: 'nineteen-cta--arrow',
  inline: 'nineteen-cta--inline',
  retro: 'nineteen-cta--retro'
};

interface Props {
  theme?: string;
  entity: Drupal_ParagraphMarketoForm | Drupal_ParagraphLink;
  primary?: boolean;
  inverse?: boolean;
}

const NineteenCta = ({ theme = '', entity, primary, inverse }: Props) => {
  const { language } = useContext(LayoutContext);
  const getMarketoLink = useMarketoLink(language);

  const hasArrow = theme === nineteenCtaThemes.arrow;
  const asLink = theme === nineteenCtaThemes.inline || hasArrow;

  if (entity.entityBundle === 'marketo_form') {
    const {
      fieldMarketoCtaText,
      fieldMarketoId
    } = entity as Drupal_ParagraphMarketoForm;

    const { url, copy } = getMarketoLink(fieldMarketoId);

    return (
      <Link
        to={url}
        primary={primary}
        secondary={!primary}
        inverse={inverse}
        asButton={!asLink}
        underline="hover"
        className={`nineteen-cta ${theme}`}
      >
        {fieldMarketoCtaText || copy}
      </Link>
    );
  }

  if (entity.entityBundle === 'link') {
    const {
      fieldCta,
      fieldOpenNewTab,
      fieldBeaconElementDetail
    } = entity as Drupal_ParagraphLink;

    let beaconEntityType;
    if (asLink) {
      beaconEntityType = MarketingPageElementType.link;
    } else if (primary) {
      beaconEntityType = MarketingPageElementType.buttonprimary;
    } else {
      beaconEntityType = MarketingPageElementType.buttonsecondary;
    }

    const beaconEvent = fieldBeaconElementDetail
      ? {
          name: 'clickedMarketingPageElement',
          elementLocation: MarketingPageElementLocation.body,
          elementDetail: fieldBeaconElementDetail,
          elementType: beaconEntityType
        }
      : null;

    if (fieldCta?.url?.path) {
      return (
        <Link
          primary={primary}
          secondary={!primary}
          hasArrow={hasArrow}
          inverse={inverse}
          asButton={!asLink}
          className={`nineteen-cta ${theme}`}
          newTab={fieldOpenNewTab || false}
          to={fieldCta.url.path}
          beaconEvent={beaconEvent}
        >
          {fieldCta.title}
        </Link>
      );
    }
  }

  return null;
};

export default NineteenCta;

export const query = graphql`
  fragment nineteenCta on Drupal_Paragraph {
    entityId
    entityBundle
    ...link
    ...marketo
  }
`;
